import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { Tooltip } from 'react-tooltip';
import { CheckIcon, ClockIcon, InformationCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import DataPlatform from '@app/model/_common/DataPlatform';
import RatesType from '@app/model/market_state/RatesType';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import Portlet from '@app/component/portlet/Portlet';
import { FormButton, Loader, Tag } from '@ui/index';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { ChannelCheckService } from '@app/model/channel_check/service/ChannelCheckService';
import {
	ChannelCheckChannel, ChannelCheckClosedRun, ChannelCheckLadderHotel,
	ChannelCheckSnapshot,
} from '@app/model/channel_check/ChannelCheckModels';
import Utilities from '@util/Utilities';
import Message from '@app/component/message/Message';
import { PROVIDER_DATA } from '@app/component/provider/FindProviderData';
import { PERIOD_DAYS, STALE_HOURS, TOP_N } from '../ChannelCheckConstants';
import { channelCheckStayLengths } from '../ChannelCheckStayLengths';
import { ChannelCheckImpactRun, formatChannelCheckPrice, orderChannelCheckRuns } from '../ChannelCheckImpact';

import { channelCheckLadderEnd, channelCheckPositionWords, hasMeasuredNoClosures } from '../ChannelCheckPresentation';

const CHANNEL_NAME_OVERRIDES: Record<string, string> = { BRAND_COM: 'Your own website', BOOKING: 'Booking.com' };
const CHANNEL_LETTERS: Record<string, string> = {
	BOOKING: 'B', BRAND_COM: 'W', EXPEDIA: 'E', TUIDE: 'TD', TUIUK: 'TU',
	TRAVELSUPERMARKET: 'TS', JET2: 'J', HOLIDAYCHECK: 'H', AIRBNB: 'A',
};
// Stable UI identity colours; these are not verified brand hex values.
const CHANNEL_BADGE_CLASSES: Record<string, string> = {
	BOOKING: 'bg-blue-900 text-white', EXPEDIA: 'bg-yellow-400 text-blue-900',
};
type RoomMode = 'cheapest' | 'type';
type ChannelMode = 'fresh' | 'all';

const channelName = (key: string) => CHANNEL_NAME_OVERRIDES[key]
	?? (PROVIDER_DATA as unknown as Record<string, { name: string }>)[key]?.name ?? key;
const channelLetter = (key: string) => CHANNEL_LETTERS[key] ?? key.charAt(0);
const formatNight = (night: string) => moment(night).format('ddd DD MMM');
const formatRange = (run: { firstNight: string, lastNight: string }) => {
	if (run.firstNight === run.lastNight) return formatNight(run.firstNight);
	const first = moment(run.firstNight);
	const last = moment(run.lastNight);
	return first.month() === last.month()
		? `${first.format('ddd DD')} – ${last.format('ddd DD MMM')}`
		: `${first.format('ddd DD MMM')} – ${last.format('ddd DD MMM')}`;
};
const formatCapture = (date: string) => moment(date).calendar(null, {
	sameDay: '[today] HH:mm', lastDay: '[yesterday] HH:mm', lastWeek: 'DD MMM HH:mm', sameElse: 'DD MMM HH:mm',
});
const ageDays = (date: string) => moment().startOf('day').diff(moment(date).startOf('day'), 'days');
const ageWords = (channels: ChannelCheckChannel[]) => {
	const days = Math.max(...channels.map(channel => ageDays(channel.captureDate)));
	return `${days} day${days === 1 ? '' : 's'} old`;
};

const Segmented = <T extends string>({ label, value, options, onChange }: {
	label: string, value: T, options: { value: T, label: string }[], onChange: (value: T) => void
}) => <span className="inline-flex items-center gap-x-2">
	<span className="text-xs text-gray-500">{label}</span>
	<span className="isolate inline-flex overflow-hidden rounded-md border border-slate-300">
		{options.map(option => <button key={option.value} type="button" onClick={() => onChange(option.value)}
			className={Utilities.classNames('whitespace-nowrap border-0 px-3 py-1.5 text-sm cursor-pointer',
				option.value === value ? 'bg-blue-600 font-semibold text-white' : 'bg-white text-slate-700 hover:bg-slate-50')}>
			{option.label}
		</button>)}
	</span>
</span>;

const ClosedRunsTable = ({ runs, selectedNights }: { runs: ChannelCheckClosedRun[], selectedNights: number }) =>
	<table className="min-w-full">
		<thead><tr className="border-b border-gray-300">
			<th className="py-3 pr-3 text-left text-sm font-semibold text-gray-900">Nights</th>
			<th className="py-3 px-3 text-left text-sm font-semibold text-gray-900">Closed on</th>
			<th className="py-3 px-3 text-left text-sm font-semibold text-gray-900">Your own website</th>
			<th className="py-3 px-3 text-left text-sm font-semibold text-gray-900">Still selling you, at</th>
		</tr></thead>
		<tbody className="divide-y divide-gray-200">{runs.map(run => {
			const lowest = Math.min(...run.alsoSelling.map(entry => entry.price));
			return <tr key={run.firstNight} className="align-top">
				<td className="py-3 pr-3 text-sm font-semibold text-gray-900 whitespace-nowrap">{formatRange(run)}
					{run.nights > 1 && <span className="block text-xs font-normal uppercase tracking-wide text-gray-400">{run.nights} nights</span>}
				</td>
				<td className="py-3 px-3"><div className="flex flex-col items-start gap-1">
					<div className="flex flex-wrap items-center gap-1">
						{run.closedOn.map(entry => <Tag key={entry.channel} state="critical">{channelName(entry.channel)} · closed</Tag>)}
						{run.stayLengthOnly.map(entry => <Tag key={entry.channel} state="info">{channelName(entry.channel)}: no {selectedNights}-night stay</Tag>)}
					</div>
					{run.noData.length > 0 && <span className="text-xs text-gray-400">no data: {run.noData.map(channelName).join(', ')}</span>}
				</div></td>
				<td className="py-3 px-3 text-sm font-medium text-gray-900">{formatChannelCheckPrice(run.directPrice)}<span className="block text-xs font-normal text-gray-400">sells</span></td>
				<td className="py-3 px-3"><div className="flex flex-wrap items-center gap-1">
					{run.alsoSelling.map(entry => <Tag key={entry.channel} state={entry.price === lowest ? 'success' : 'gray'}>
						{channelName(entry.channel)} <b className="ml-1">{formatChannelCheckPrice(entry.price)}</b>
						{entry.price === lowest && run.alsoSelling.length > 1 && <span className="ml-1">· lowest</span>}
					</Tag>)}
				</div></td>
			</tr>;
		})}</tbody>
	</table>;

const Pin = ({ letter, className }: { letter: string, className: string }) =>
	<span className={Utilities.classNames('inline-flex h-5 min-w-fit items-center justify-center rounded-full px-1.5 text-xs font-bold', className)}>{letter}</span>;

const LadderColumn = ({ channel, otherChannel, hotels, mainClass, mainWords }: {
	channel: string, otherChannel: string, hotels: ChannelCheckLadderHotel[],
	mainClass: string, mainWords: string
}) => {
	const sortedHotels = [...hotels].sort((a, b) => b.price - a.price);
	return <div className="flex flex-col gap-y-1">
	<div className="mb-1 flex items-center gap-x-2"><Pin letter={channelLetter(channel)} className={CHANNEL_BADGE_CLASSES[channel] ?? 'bg-gray-700 text-white'} />
		<span className="text-xs font-semibold text-gray-900">{channelName(channel)}</span><span className="text-xs text-gray-400">most expensive first</span>
	</div>
	{sortedHotels.map(hotel => {
		const rowClass = hotel.isMain ? mainClass : !hotel.inLadder ? 'text-gray-400' : 'text-gray-500';
		return <div key={hotel.name} className={Utilities.classNames('flex justify-between gap-x-3 rounded-md px-2 py-1 text-sm', rowClass)}>
			<span>{hotel.isMain ? `You · ${mainWords}` : hotel.name}
				{!hotel.isMain && !hotel.inLadder && <span className="ml-1 text-xs">· not on {channelName(otherChannel)}, not in the ladder</span>}
				{hotel.isSuspect && <Tag state="warning" extraClassName="ml-1">suspect price</Tag>}
			</span><span className="whitespace-nowrap">{formatChannelCheckPrice(hotel.price)}</span>
		</div>;
	})}
</div>;
};

const ShiftCard = ({ entry, referenceChannel }: { entry: ChannelCheckImpactRun, referenceChannel: string }) => {
	const { run } = entry;
	const cheaperOnChannel = run.chPrice < run.refPrice;
	const ladderEnd = channelCheckLadderEnd(run.refRank, run.setSize);
	const rankWords = (rank: number) => channelCheckPositionWords(rank, run.setSize, ladderEnd);
	const refMainClass = cheaperOnChannel ? 'bg-rose-600 font-semibold text-white' : 'bg-emerald-600 font-semibold text-white';
	const chMainClass = cheaperOnChannel ? 'bg-emerald-600 font-semibold text-white' : 'bg-rose-600 font-semibold text-white';
	return <div className="flex flex-col gap-6 rounded-lg border border-gray-200 p-4 lg:flex-row">
		<div className="w-full flex-none lg:w-44">
			<span className="text-sm font-semibold text-gray-900">{formatRange(run)}
				{run.nights > 1 && <span className="block text-xs font-normal uppercase tracking-wide text-gray-400">{run.nights} nights</span>}
			</span>
		</div>
		<div className="grid flex-1 grid-cols-1 gap-5 lg:grid-cols-2">
			<LadderColumn channel={referenceChannel} otherChannel={run.channel} hotels={run.refLadder} mainClass={refMainClass} mainWords={rankWords(run.refRank)} />
			<LadderColumn channel={run.channel} otherChannel={referenceChannel} hotels={run.chLadder} mainClass={chMainClass} mainWords={rankWords(run.chRank)} />
		</div>
	</div>;
};

const ExpandableRuns = ({ total, isExpanded, onToggle }: { total: number, isExpanded: boolean, onToggle: () => void }) =>
	total <= TOP_N ? null : <FormButton state="white" size="small" onClick={onToggle}>{isExpanded ? 'Show fewer' : `Show all ${total}`}</FormButton>;

const ChannelCheckPage: React.FunctionComponent = () => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { filter } = useMarketStateFilterInputTools();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isClosedExpanded, setIsClosedExpanded] = useState(false);
	const [isShiftsExpanded, setIsShiftsExpanded] = useState(false);
	const stayLengths = channelCheckStayLengths(companyHotel);
	const roomTypes = companyHotel.orderedRoomMappingTypeConfigurations()
		.filter(config => config.isEnabled && config.type !== HotelRoomMappingType.NONE);
	const nightsParam = Number(searchParams.get('nights') ?? 0);
	const selectedNights: number | undefined = stayLengths.includes(nightsParam) ? nightsParam : stayLengths[0];
	const roomMode: RoomMode = searchParams.get('room') === 'type' && roomTypes.length > 0 ? 'type' : 'cheapest';
	const roomTypeParam = searchParams.get('roomType') ?? '';
	const selectedRoomType = roomTypes.find(config => config.type === roomTypeParam)?.type ?? roomTypes[0]?.type;
	const channelMode: ChannelMode = searchParams.get('channels') === 'all' ? 'all' : 'fresh';
	const setParam = (changes: Record<string, string | null>, replace = false) => {
		const next = new URLSearchParams(searchParams);
		Object.entries(changes).forEach(([key, value]) => value === null ? next.delete(key) : next.set(key, value));
		setSearchParams(next, { replace });
	};

	useEffect(() => {
		const changes: Record<string, string | null> = {};
		if (selectedNights !== undefined && nightsParam !== selectedNights) changes.nights = String(selectedNights);
		if (roomMode === 'type' && selectedRoomType !== undefined && roomTypeParam !== selectedRoomType) changes.roomType = selectedRoomType;
		if (Object.keys(changes).length > 0) setParam(changes, true);
	}, [selectedNights, nightsParam, roomMode, selectedRoomType, roomTypeParam]);

	const startStatDate = moment.utc().format('YYYY-MM-DD');
	const endStatDate = moment.utc().add(PERIOD_DAYS - 1, 'days').format('YYYY-MM-DD');
	const filterInput: MarketStateFilterInput = {
		roomTypes: roomMode === 'type' && selectedRoomType !== undefined ? [selectedRoomType] : [], provider: undefined,
		platform: DataPlatform.WEB, region: filter.region, ratesType: RatesType.PUBLIC,
		nights: selectedNights, airportCode: filter.airportCode, competitorTagIds: [],
	};
	const includeStaleChannels = channelMode === 'all';
	const { data, isLoading, isError } = useQuery({
		queryKey: ['channel-check', companyHotel.id, startStatDate, endStatDate, filterInput, includeStaleChannels],
		queryFn: () => ChannelCheckService.findForPeriod(companyHotel.id, startStatDate, endStatDate, filterInput, includeStaleChannels),
		enabled: selectedNights !== undefined, refetchOnWindowFocus: false,
	});

	const orderedShiftRuns = useMemo(() => orderChannelCheckRuns(data?.shiftRuns ?? []), [data?.shiftRuns]);

	if (selectedNights === undefined) return <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"><Message state="warning">Channel check is unavailable because this hotel has no configured stay length.</Message></div>;
	if (isError) return <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"><Message state="error">Loading the channel check failed. Refresh the page to try again.</Message></div>;
	if (isLoading || !data) return <Loader />;
	const snapshot: ChannelCheckSnapshot = data;
	const closedRuns = isClosedExpanded ? snapshot.closedRuns : snapshot.closedRuns.slice(0, TOP_N);
	const shiftRuns = isShiftsExpanded ? orderedShiftRuns : orderedShiftRuns.slice(0, TOP_N);
	const freshCount = snapshot.channels.filter(channel => !channel.isStale).length;
	const comparedFresh = snapshot.channels.filter(channel => !channel.isStale && channel.notComparedReason === null);
	const noMappedRoom = snapshot.channels.filter(channel => channel.notComparedReason === 'NO_MAPPED_ROOM');
	const stale = snapshot.channels.filter(channel => channel.isStale);
	const staleCompared = stale.length > 0 && stale.every(channel => channel.notComparedReason === null);
	const hotelOnlyCompared = snapshot.channels.filter(channel => channel.key !== snapshot.referenceChannel && !channel.isPackage && channel.notComparedReason === null);
	const packageChannels = snapshot.channels.filter(channel => channel.isPackage && channel.notComparedReason === null);
	const roomTypeName = selectedRoomType !== undefined ? companyHotel.findRoomMappingTypeName(selectedRoomType) : '';
	const latestCapture = (channels: ChannelCheckChannel[]) => channels.map(channel => channel.captureDate).sort().reverse()[0];
	const captureRange = (channels: ChannelCheckChannel[]) => {
		const dates = channels.map(channel => channel.captureDate).sort();
		const first = moment(dates[0]);
		const last = moment(dates[dates.length - 1]);
		return first.isSame(last, 'day') ? `${first.format('DD MMM HH:mm')}–${last.format('HH:mm')}`
			: `${first.format('DD MMM HH:mm')} – ${last.format('DD MMM HH:mm')}`;
	};
	const helpText = `Fresh = captured in the last ${STALE_HOURS} hours. All = every channel, stale ones included. Cheapest compares the cheapest available room. Same type uses your room mapping.`;

	return <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 flex flex-col gap-y-4">
		<div className="rounded-lg bg-white p-4 shadow">
			<div className="flex flex-wrap items-center gap-x-5 gap-y-3">
				<h2 className="text-base font-semibold text-gray-900">Channel check</h2>
				{stayLengths.length > 1 && <Segmented label="Stay" value={String(selectedNights)} options={stayLengths.map(nights => ({ value: String(nights), label: `${nights} nights` }))} onChange={value => setParam({ nights: value })} />}
				{roomTypes.length > 0 && <span className="inline-flex items-center gap-x-2">
					<Segmented<RoomMode> label="Room" value={roomMode} options={[{ value: 'cheapest', label: 'Cheapest' }, { value: 'type', label: 'Same type' }]} onChange={value => setParam({ room: value === 'cheapest' ? null : value, roomType: value === 'cheapest' ? null : String(selectedRoomType) })} />
					{roomMode === 'type' && <select aria-label="Room type" className="input py-1.5 text-sm" value={selectedRoomType} onChange={event => setParam({ roomType: event.target.value })}>
						{roomTypes.map(config => <option key={config.type} value={config.type}>{companyHotel.findRoomMappingTypeName(config.type)}</option>)}
					</select>}
				</span>}
				<Segmented<ChannelMode> label="Channels" value={channelMode} options={[{ value: 'fresh', label: `Fresh · ${freshCount}` }, { value: 'all', label: `All · ${snapshot.channels.length}` }]} onChange={value => setParam({ channels: value === 'fresh' ? null : value })} />
				<span className="text-sm text-gray-500 lg:ml-auto">{moment(snapshot.periodFrom).format('DD MMM')} – {moment(snapshot.periodTo).format('DD MMM YYYY')} · Reference: <b className="text-gray-900">{channelName(snapshot.referenceChannel)}</b></span>
			</div>
			{roomMode === 'type' && <div className="mt-3 flex items-center gap-x-3 rounded-md bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-900 ring-1 ring-inset ring-blue-700/10">
				<InformationCircleIcon className="h-5 w-5 flex-none text-blue-700" />Same type: only rooms in your room mapping are compared, as <b>{roomTypeName}</b>.
			</div>}
			<div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-gray-200 pt-3 text-xs">
				{comparedFresh.length > 0 && <span className="inline-flex items-center gap-x-1.5 text-gray-700"><CheckIcon className="h-4 w-4 text-emerald-600" /><b>Compared</b><span className="text-gray-500">{comparedFresh.map(channel => channelName(channel.key)).join(', ')} · captured {formatCapture(latestCapture(comparedFresh))}</span></span>}
				{noMappedRoom.length > 0 && <span className="inline-flex items-center gap-x-1.5 text-blue-800"><InformationCircleIcon className="h-4 w-4" /><b>Not compared, no mapped room</b><span>{noMappedRoom.map(channel => channelName(channel.key)).join(', ')}</span></span>}
				{stale.length > 0 && <span className="inline-flex items-center gap-x-1.5 text-amber-800"><ClockIcon className="h-4 w-4" /><b>{staleCompared ? 'Compared' : 'Not compared'}, {ageWords(stale)}</b><span>{stale.map(channel => channelName(channel.key)).join(', ')} · captured {captureRange(stale)}</span></span>}
				<span className="ml-auto" data-tooltip-id="channel-check-help" data-tooltip-content={helpText}><QuestionMarkCircleIcon className="h-4 w-4 cursor-help text-gray-400" /></span>
				<Tooltip id="channel-check-help" className="max-w-xs" />
			</div>
		</div>

		<>{hasMeasuredNoClosures(snapshot) ? <p className="flex items-center gap-2 text-sm text-gray-900">
			<CheckIcon className="h-5 w-5 text-emerald-600" aria-hidden="true" />Channel closures: none in this period.
		</p> : <Portlet isLarge title="Nights a channel closes you while your own website sells" note={<span className="ml-3"><b className="text-base text-gray-900">{snapshot.closedNights}</b> nights in {snapshot.closedRuns.length} runs</span>}>
			{snapshot.closedEmptyReason !== null && <p className="text-sm text-gray-400">{snapshot.closedEmptyReason}</p>}
			{snapshot.closedEmptyReason === null && snapshot.closedRuns.length > 0 && <div className="overflow-x-auto"><ClosedRunsTable runs={closedRuns} selectedNights={selectedNights} /></div>}
			{snapshot.closedEmptyReason === null && closedRuns.length > 0 && <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-200 pt-3"><ExpandableRuns total={snapshot.closedRuns.length} isExpanded={isClosedExpanded} onToggle={() => setIsClosedExpanded(!isClosedExpanded)} />
				<span className="text-xs text-gray-400"><span className="font-semibold text-rose-700">closed</span> = no bookable room for this stay · <span className="font-semibold text-blue-700">no N-night stay</span> = another stay length is available · <b>no data</b> = not captured</span>
			</div>}
		</Portlet>}</>

		<Portlet isLarge title={roomMode === 'type' ? `Nights your ${roomTypeName} room ranks differently than on ${channelName(snapshot.referenceChannel)}` : `Nights you rank differently than on ${channelName(snapshot.referenceChannel)}`}
			note={<span className="ml-3 inline-flex flex-wrap items-center gap-x-2"><span><b className="text-base text-gray-900">{snapshot.shiftNights}</b> nights in {snapshot.shiftRuns.length} runs · {channelName(snapshot.referenceChannel)} is the reference</span>{roomMode === 'type' && <Tag state="info">{roomTypeName} · mapped rooms only</Tag>}</span>}>
			<div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500"><span>Hotel-only channels compared:</span>
				{hotelOnlyCompared.map(channel => <Tag key={channel.key} state="gray">{channelName(channel.key)}</Tag>)}
				{hotelOnlyCompared.length === 0 && <span className="text-gray-400">none</span>}
				{stale.filter(channel => channel.notComparedReason === 'STALE').map(channel => <Tag key={channel.key} state="warning">{channelName(channel.key)} · stale</Tag>)}
				{noMappedRoom.map(channel => <Tag key={channel.key} state="info">{channelName(channel.key)} · no mapped room</Tag>)}
				{packageChannels.map(channel => <Tag key={channel.key} state="none">{channelName(channel.key)} · flight included</Tag>)}
			</div>
			{snapshot.shiftRuns.length > 0 && <p className="mb-3 text-xs text-gray-500">Biggest place difference first. Suspect prices last.</p>}
			{snapshot.shiftEmptyReason !== null && <p className="text-sm text-gray-400">{snapshot.shiftEmptyReason}</p>}
			{snapshot.shiftEmptyReason === null && snapshot.shiftRuns.length > 0 && <div className="flex flex-col gap-y-3">{shiftRuns.map(entry => <ShiftCard key={`${entry.run.firstNight}-${entry.run.channel}`} entry={entry} referenceChannel={snapshot.referenceChannel} />)}</div>}
			<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-200 pt-3"><ExpandableRuns total={snapshot.shiftRuns.length} isExpanded={isShiftsExpanded} onToggle={() => setIsShiftsExpanded(!isShiftsExpanded)} />
				<span className="text-xs text-gray-400">A greyed hotel is priced on one channel only.</span>
			</div>
			{roomMode === 'cheapest' && <p className="mt-3 text-xs leading-5 text-gray-400">Only Booking.com reports a room type. Every other channel reports <code>NONE</code>, so this comparison is not like-for-like. Prices more than 30% from the other-channel median carry a suspect flag.</p>}
		</Portlet>
	</div>;
};

export default ChannelCheckPage;
